import { ButtonBase } from '@mui/material'
import type { FC, HTMLAttributes, ReactNode, Ref } from 'react'
import { createContext, useContext, useState } from 'react'
import { Button as AriaButton } from 'react-aria-components'
import { Link } from 'react-router'
import FluentMoreVertical20Filled from '~icons/fluent/more-vertical-20-filled'
import FluentShare20Regular from '~icons/fluent/share-20-regular'
import IcBaselineDone from '~icons/ic/baseline-done'
import IcRoundExplicit from '~icons/ic/round-explicit'
import {
  UIMenu,
  UIMenuItem,
  type UIMenuItemProps,
  UIMenuSeparatorSymbol,
  UIMenuTrigger,
} from 'src/components/ui/Menu'
import Text from 'src/components/ui/Text'
import UserAvatar from 'src/components/UserAvatar'
import {
  type TackPreviewFragment,
  type Artwork,
  ArtworkVisibilityType,
  type Media as MediaType,
  type User,
} from 'src/services/apollo/generated/graphql'
import {
  getFaceCenterObjectPosition,
  isAnimatedArtwork,
} from 'src/utils/artwork'
import { cx } from 'src/utils/cx'
import dayjs from 'src/utils/dayjs'
import { track } from 'src/utils/tracker'
import { openShareArtworkDialog } from '../ArtworkDetail/ArtworkShare/ArtworkShareDialog'
import { getArtworkAlt } from '../ArtworkDetail/utils'
import MediaPreview, { type MediaPreviewProps } from '../Common/MediaPreview'
import type { MediaContentRemovedProps } from '../Media/MediaContentRemoved'
import MediaContentRemoved from '../Media/MediaContentRemoved'
import { ArtworkFrameForArtworkCard } from './ArtworkFrame'
import ArtworkLikeButton from './ArtworkLikeButton'

export interface BaseArtworkItemPreviewCardProps extends HTMLAttributes<HTMLDivElement> {
  mediaId?: string
  media?: MediaType
  mediaFallback?: ReactNode
  mediaExtra?: ReactNode
  /**
   * Like `mediaExtra` but rendered outside the card's anchor element.
   * Use this for any `<Link>` / `<a>` elements overlaid on the image to
   * avoid invalid nested-anchor HTML.  Positioned over the image area only
   * (excludes the h-14 info section when showInfo is true).
   *
   * The wrapper uses `pointer-events-none`; interactive descendants must
   * opt in with `pointer-events-auto` to be clickable.
   */
  mediaExtraLinks?: ReactNode

  label?: ReactNode
  secondLabel?: ReactNode
  time?: string
  showInfo?: boolean
  artwork?: Pick<
    Artwork,
    | 'id'
    | 'liked'
    | 'likedCount'
    | 'type'
    | 'extra'
    | 'mediaId'
    | 'videoMediaId'
    | 'author'
    | 'visibility'
    | 'title'
    | 'media'
    | 'hidePrompts'
  > & {
    tacks?: TackPreviewFragment[] | null
  }
  animated?: boolean
  overrideAuthor?: User
  showLikeBtn?: boolean
  showAuthorAvatar?: boolean
  loading?: boolean
  to?: string
  isEdit?: boolean
  selected?: boolean
  target?: '_blank'
  contentClassName?: string
  borderClassName?: string
  likeBtn?: React.ReactNode
  /**
   * 'author': Follow author's setting / membership a.k.a Artwork Frame.
   *  Will always fallback to 'plain' if author doesn't have a frame to show.
   *
   * 'plain': gray border
   */
  border?: 'author' | 'plain' | false
  showIndex?: boolean
  index?: number
  fit?: 'contain' | 'cover'

  /**
   * Use natural aspect ratio instead of forcing square
   * Used in waterfall layout mode
   */
  naturalAspectRatio?: boolean

  /**
   * Ref of last element. Used for infinite scrolling
   */
  lastElementRef?: Ref<HTMLDivElement>
  wrapperRef?: Ref<HTMLDivElement>

  /**
   * When model cover is under moderation
   */
  mediaContentRemovedOverlayType?: MediaContentRemovedProps['overlayType']

  // Sensitivity
  nsfwBlur?: boolean
  /**
   * Only effective when nsfwBlur is true
   */
  showExplicitIcon?: boolean
  // Web doesn't care about archived artworks for now
  archived?: boolean

  menu?: Array<UIMenuItemProps | Falsy>
  hideShare?: boolean

  /** feed is necessary for recommendation tracking */
  trackingArgs?: Record<string, unknown> & { feed?: string; feedUuid?: string }
  contentExtra?: JSX.Element

  /** Will be used for artwork image's alt if provided */
  modelTitle?: string

  /** Apply disabled to ButtonBase */
  disabled?: boolean

  overlay?: ReactNode

  mediaLoading?: 'lazy' | 'eager'
  mediaFetchPriority?: 'auto' | 'high' | 'low'
  imgProps?: MediaPreviewProps['imgProps']
}

const BaseArtworkItemPreviewCardContext =
  createContext<BaseArtworkItemPreviewCardProps>(null as never)

const BaseArtworkItemPreviewCard: FC<
  BaseArtworkItemPreviewCardProps
> = props => {
  const {
    mediaId,
    media,
    mediaFallback,
    mediaExtra,
    mediaExtraLinks,

    label,
    secondLabel,
    time,
    showInfo = true,
    artwork,
    animated,
    overrideAuthor,
    className,
    contentClassName,
    borderClassName,
    showLikeBtn,
    showAuthorAvatar,
    loading,
    to,
    lastElementRef,
    wrapperRef,
    selected = false,
    isEdit = false,
    likeBtn,
    trackingArgs,
    border,
    contentExtra,
    fit = 'cover',
    naturalAspectRatio,

    mediaContentRemovedOverlayType,
    nsfwBlur,
    showExplicitIcon,

    // Exclude unsupported props for <ButtonBase>
    /* eslint-disable @typescript-eslint/no-unused-vars */
    menu,
    hideShare,

    modelTitle,

    overlay,
    mediaLoading = 'lazy',
    mediaFetchPriority = 'auto',

    imgProps,

    ...rest
  } = props

  const isAnimated = animated || isAnimatedArtwork(artwork)
  const [preferStill, _setPreferStill] = useState(true)
  const setPreferStill = (value: boolean) => {
    if (isAnimated) {
      _setPreferStill(value)
    }
  }

  const artworkLikeBtn =
    showLikeBtn &&
    (likeBtn ??
      (artwork && (
        <ArtworkLikeButton
          trackingArgs={trackingArgs}
          artworkId={artwork.id}
          initialCount={artwork.likedCount}
          initialValue={artwork.liked}
          numOnTop
          hideValueOnZero
        />
      )))

  const mediaContent =
    mediaContentRemovedOverlayType != null ? (
      <div className="w-full h-full flex flex-col items-center justify-center p-2">
        <MediaContentRemoved
          type="in-grid"
          overlayType={mediaContentRemovedOverlayType}
        />
      </div>
    ) : (
      <>
        {mediaId ? (
          <MediaPreview
            className={cx('w-full h-full', nsfwBlur && 'blur-xl')}
            blurHash={artwork?.extra?.imageBlurHash}
            mediaId={mediaId ?? artwork?.mediaId}
            media={media}
            videoMediaId={artwork?.videoMediaId}
            thumbnail
            fit={fit}
            imageLoading={mediaLoading}
            imageFetchPriority={mediaFetchPriority}
            objectPosition={
              fit === 'cover'
                ? getFaceCenterObjectPosition(
                    artwork?.extra?.faceCenter,
                    media?.width,
                    media?.height,
                  )
                : undefined
            }
            preferStill={preferStill}
            alt={getArtworkAlt(artwork, modelTitle)}
            imgProps={imgProps}
          />
        ) : (
          mediaFallback
        )}

        <PreviewMask nsfwBlur={nsfwBlur} showExplicitIcon={showExplicitIcon} />
      </>
    )

  return (
    <BaseArtworkItemPreviewCardContext.Provider value={props}>
      <article
        className={cx(
          // FIXME: `group` here can confuse nested components
          'group group/card',
          'relative isolate',
          border && 'p-0.5',
        )}
      >
        {border === 'author' ? (
          <ArtworkFrameForArtworkCard
            user={artwork?.author}
            borderClassName={borderClassName}
            decorations={{
              frame: true,
            }}
          />
        ) : border === 'plain' ? (
          <div
            className={cx(
              'z-[-1] absolute inset-px rounded-lg',
              'bg-black/10 group-hover:bg-black/30',
              'transition ease-out duration-300 group-hover:shadow-xl',
              borderClassName,
            )}
          />
        ) : (
          <div
            className={cx(
              'z-[-1] absolute inset-0 rounded-lg',
              'transition ease-out duration-300 group-hover:shadow-2xl',
              borderClassName,
            )}
          />
        )}

        <ButtonBase
          ref={wrapperRef}
          component={to != null ? Link : 'div'}
          className={cx(
            'group block bg-background-secondary transition',
            'cursor-pointer hover:no-underline h-full',
            border === 'author' ? 'rounded-md' : 'rounded-lg',
            contentClassName,
          )}
          to={to}
          {...rest}
          // Apply hover-to-switch behavior to animated artworks only to prevent unnecessary flickering
          onMouseEnter={() => setPreferStill(false)}
          onMouseLeave={() => setPreferStill(true)}
        >
          <div
            ref={lastElementRef}
            className={cx(
              'relative w-full bg-active overflow-hidden transition duration-300',
              naturalAspectRatio ? '' : 'aspect-square',
              isEdit && selected && 'border-black border-[5px] rounded-[16px]',
              showInfo
                ? border === 'author'
                  ? 'rounded-t-md'
                  : 'rounded-t-lg'
                : border === 'author'
                  ? 'rounded-md'
                  : 'rounded-lg',
              className,
            )}
            style={
              naturalAspectRatio && media?.width && media?.height
                ? { aspectRatio: `${media.width} / ${media.height}` }
                : undefined
            }
          >
            <div className="absolute inset-0 w-full h-full">
              {mediaContent}
              {mediaExtra}
            </div>

            {isEdit && (
              <div className="w-full absolute top-0 p-3">
                <IcBaselineDone
                  className={cx(
                    selected ? 'text-opacity-100' : 'text-opacity-0',
                    'w-7 h-7 border-2 border-black bg-white rounded-xl float-right text-black',
                  )}
                />
              </div>
            )}

            {artworkLikeBtn ? (
              <div
                className={cx(
                  'absolute bottom-0 right-0 p-3 text-white drop-shadow',
                  showInfo && 'md:hidden',
                )}
              >
                <div className="block translate-y-1">{artworkLikeBtn}</div>
              </div>
            ) : null}
          </div>

          {showInfo && (
            // h-14 is for avoiding layout shift
            <div className="relative flex items-center gap-1.5 w-full h-14 pl-2 pb-0.5">
              {showAuthorAvatar && (
                <UserAvatar
                  className="flex-none shrink-0 size-8"
                  user={artwork?.author ?? overrideAuthor}
                  decorationSize="small"
                />
              )}

              <div className="flex flex-col items-stretch gap-0.5 min-w-0 px-1 py-1.5">
                <div
                  className={cx(
                    'text-left min-h-[1rem] text-sm font-semibold truncate',
                    loading && 'bg-active animate-pulse',
                  )}
                >
                  {label}
                </div>

                <div
                  className={cx(
                    'text-left w-auto min-h-[1rem] text-xs text-primary-light truncate',
                    loading && 'bg-active animate-pulse',
                  )}
                >
                  {secondLabel || (time ? dayjs(time).format('lll') : '')}
                </div>
              </div>

              <div className="flex-1"></div>

              <div className="flex-shrink-0 mr-4 hidden md:block">
                {artworkLikeBtn}
              </div>
            </div>
          )}

          {contentExtra}
        </ButtonBase>
        {!isEdit && <MenuButton />}

        {mediaExtraLinks && (
          <div
            className={cx(
              border && 'p-0.5',
              'absolute inset-0 z-10 pointer-events-none',
              showInfo && 'bottom-14',
            )}
          >
            {mediaExtraLinks}
          </div>
        )}

        {/* Overlay elements are absolutely positioned to cover the entire card area.
         * These styles ensure the overlay aligns correctly with borders and preserves layout integrity.
         * Please contact @Asuka first if you plan to refactor this section.
         */}
        {overlay ? (
          <div className={cx(border && 'p-0.5', 'absolute inset-0 z-20')}>
            {overlay}
          </div>
        ) : null}
      </article>
    </BaseArtworkItemPreviewCardContext.Provider>
  )
}

export default BaseArtworkItemPreviewCard

function MenuButton() {
  const { artwork, trackingArgs, menu, hideShare } = useContext(
    BaseArtworkItemPreviewCardContext,
  )

  const items = UIMenu.compact<UIMenuItemProps>([
    artwork &&
      artwork.visibility === ArtworkVisibilityType.Public &&
      !hideShare && {
        id: 'share',
        icon: <FluentShare20Regular className="size-5" />,
        children: <Text i18nKey="common:share.label" />,
        onAction: () => {
          track('share_my_work_click', trackingArgs)
          openShareArtworkDialog(artwork, {
            trackingArgs,
          })
        },
      },
    menu && UIMenuSeparatorSymbol,
    ...(menu ?? []),
  ])

  if (items.length === 0) return null

  return (
    <>
      <UIMenuTrigger>
        <AriaButton
          className={cx(
            'absolute top-1.5 right-1.5 inline-grid place-items-center size-7 rounded-md ring-1 ring-white/20 transition duration-100',
            // Default state (invisible when able to hover)
            'text-white/50 bg-black/50 [@media(hover:hover)]:opacity-0',
            // Button appears
            'group-hover/card:opacity-100',
            // Button is active
            'hover:text-white data-[pressed]:text-white hover:bg-black/80 data-[pressed]:bg-black/80 data-[pressed]:opacity-100',
          )}
        >
          <FluentMoreVertical20Filled className="size-5" />
        </AriaButton>
        <UIMenu items={items} placement="bottom right">
          {props => <UIMenuItem {...props} />}
        </UIMenu>
      </UIMenuTrigger>
    </>
  )
}

export function PreviewMask(props: {
  nsfwBlur?: boolean
  showExplicitIcon?: boolean
}) {
  if (props.nsfwBlur)
    return <NsfwMask showExplicitIcon={props.showExplicitIcon} />
  // Archived mask is disabled for web
  // if (props.markedExplicit) return <ExplicitMask />
  return null
}

function NsfwMask(props: { showExplicitIcon?: boolean }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-black text-white bg-opacity-40 group-hover:bg-opacity-20 transition">
      {props.showExplicitIcon && (
        <IcRoundExplicit className="opacity-50 group-hover:opacity-80 transition-opacity text-3xl" />
      )}
    </div>
  )
}
